/** @jsxImportSource theme-ui */
import { FunctionComponent } from 'react';

import { IClickable, IWithClassName } from '../common-props';

type FlexGroupProps = IClickable &
  IWithClassName & {
    /** The prefix component to the content */
    prefix?: React.ReactNode;
    /** The suffix component to the content */
    suffix?: React.ReactNode;
    /**
     * How to align the items in the flex group.
     * @default baseline
     */
    alignItems?: 'center' | 'baseline' | 'end' | 'flex-start' | 'flex-end';
    /**
     * The space between the main content & the prefix/suffix
     * @default 6px
     */
    gutter?: string;
    /**
     * If true: the flex direction is "column"
     * If "inverted": the flex direction is "column-reverse"
     * If false: the flex direction is row
     * @default false
     */
    stackable?: boolean | 'inverted';
    /**
     * Removes the default minimum width override
     * @default false
     */
    noMinWidth?: boolean;
  };

export const FlexGroup: FunctionComponent<FlexGroupProps> = ({
  prefix,
  suffix,
  children,
  alignItems,
  gutter = '6px',
  stackable = false,
  noMinWidth = false,
  onClick,
  ...props
}) => {
  const sxCommon = {
    flexShrink: 0,
    ...(!noMinWidth && { minWidth: '10px' }),
  };
  return (
    <div
      {...props}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: alignItems || 'baseline',
        ...(stackable && {
          flexDirection: [stackable === 'inverted' ? 'column-reverse' : 'column', 'row'],
          '& .prefix': {
            mr: [0, gutter],
          },
          '& .suffix': {
            ml: [0, gutter],
          },
        }),
      }}
    >
      {prefix && (
        <div className="prefix" sx={{ ...sxCommon, mr: gutter }}>
          {prefix}
        </div>
      )}
      <div sx={{ flexGrow: 1 }}>{children}</div>
      {suffix && (
        <div className="suffix" sx={{ ...sxCommon, ml: gutter }}>
          {suffix}
        </div>
      )}
    </div>
  );
};

export default FlexGroup;
